import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";

import { CentralIntakeService } from "../../../services/central-intake.service";
import {
    IAgencyBranchesResponse,
    IInquiryLocationsResponse,
    IReferralLocationsResponse
} from "../../vue-interfaces/i-get-inquiry";
import { IBranchResponse } from "../../vue-interfaces/i-branch";
import { formatDate } from "../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { ParentPermissionEnum } from "../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../enums/PermissionActionEnum";
import { ToastrService } from "../../../services/toastr.service";
import { BranchesService } from "../../../services/branches-service";
import { IPromptOptions } from "../../vue-interfaces/i-prompt";

import ErrorMessageHandler from "../../vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import EmptyTextHandler from "../../vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";
import Prompt from "../../vue-dialogs/prompt/prompt.vue";

export default defineComponent({
    components: {
        ErrorMessageHandler,
        EmptyTextHandler
    },

    inject: ["dialogRef"],

    data(vm) {
        return {
            _toastrService: null as ToastrService,
            _permissionManager: null as PermissionManager,
            _centralIntakeService: null as CentralIntakeService,
            _branchesService: null as BranchesService,
            referralLocations: [] as IReferralLocationsResponse[],
            allBranchList: [] as IBranchResponse[],
            isLoading: false as boolean,
            isError: false as boolean,
            referralDetails: null,
            dialogReference: this.dialogRef as any,
            canEditReferralLocation: false as boolean,
            isEditRequested: false as boolean,
            isSaving: false as boolean,
            agencyBranches: [] as IBranchResponse[],
            selectedAgencyBranch: null as string,
            selectedLocation: null as IReferralLocationsResponse,
            formatDate
        };
    },

    created() {
        this._centralIntakeService = Container.instance.get(CentralIntakeService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._toastrService = Container.instance.get(ToastrService);
        this._branchesService = Container.instance.get(BranchesService);
        this.referralDetails = this.dialogReference.data.referralDetails;
    },

    async mounted() {
        this.getReferralBranches();
        this.canEditReferralLocation = this._permissionManager.checkPermission(
            ParentPermissionEnum.inquiries,
            PermissionActionEnum.canEditBranches
        );
        if (this.canEditReferralLocation) {
            this.allBranchList = await this._branchesService.getAllBranches();
            this.agencyBranches = this.allBranchList.filter(
                (branch) => branch.accountId === this.referralDetails.accountId
            );
        }
    },
    computed: {
        hasData() {
            return this.referralLocations.length;
        },
        columns() {
            return this.canEditReferralLocation ? 8 : 7;
        }
    },
    methods: {
        async getReferralBranches() {
            try {
                this.isLoading = true;
                const payload = {
                    agencyId: this.referralDetails.accountId,
                    application: this.referralDetails.application,
                    referralId: this.referralDetails.id
                };
                const response = await this._centralIntakeService.getReferralLocations(payload);
                this.referralLocations = response;
            } catch (err) {
                console.error(err);
                this.isError = true;
            } finally {
                this.isLoading = false;
            }
        },
        closeDialog() {
            this.dialogReference.close();
        },
        handleEditRequest(location) {
            this.isEditRequested = true;
            this.selectedLocation = location;
        },
        cancelEditLocation() {
            this.isEditRequested = false;
            this.selectedLocation = null;
        },
        async updateReferralBranch(location) {
            const payload = {
                AgencyId: this.referralDetails.accountId,
                Application: this.referralDetails.application,
                AgencyLocationId: this.selectedAgencyBranch,
                Id: location.Id,
                ReferralId: this.referralDetails.id
            };
            let promptOptions: IPromptOptions = {
                message:
                    "All documentation will reflect the updated branch information starting from the branch’s effective date. Do you want to save this branch?",
                okText: "Yes",
                cancelText: "No"
            };
            this.$dialog.open(Prompt, {
                props: {
                    modal: true,
                    contentStyle: {
                        overflowX: "hidden"
                    },
                    showHeader: false
                },
                data: {
                    promptOptions
                },
                onClose: async (confirm) => {
                    if (confirm.data === "success") {
                        try {
                            this.isSaving = true;
                            const response = await this._centralIntakeService.updateReferralLocation(payload);
                            this._toastrService.success({
                                title: "Updated!!",
                                message: `${response.ErrorMessage}`
                            });
                        } catch (err) {
                            console.error(err);
                            this._toastrService.success({
                                title: "Failed!!",
                                message: `Failed to update inquiry location . Try Again!!`
                            });
                        } finally {
                            this.isSaving = false;
                            this.isEditRequested = false;
                            this.selectedLocation = null;
                            this.getReferralBranches();
                        }
                    }
                }
            });
        }
    }
});
